/**
 * useChatSocket — WebSocket hook for a single chat room.
 *
 * Usage:
 *   const { connected, sendMessage, sendTyping, markRead } = useChatSocket(roomId, onEvent);
 *
 * `onEvent` is called with incoming frames:
 *   { type: 'message', id, room_id, content, sender, created_at }
 *   { type: 'typing',  user_id, room_id }
 *
 * Reconnects automatically with exponential back-off.
 * Socket is re-opened whenever `roomId` changes or the access token changes.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

const INITIAL_DELAY = 1_000;
const MAX_DELAY     = 30_000;

type SocketEvent = Record<string, unknown>;

function getWsUrl(roomId: number | string) {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/chat/${roomId}/`;
}

export function useChatSocket(roomId: number | string | null | undefined, onEvent: (event: SocketEvent) => void) {
  const [connected, setConnected] = useState(false);
  const onEventRef   = useRef(onEvent);
  const wsRef        = useRef(null);
  const delayRef     = useRef(INITIAL_DELAY);
  const timerRef     = useRef(null);
  const unmountedRef = useRef(false);

  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  const open = useCallback(() => {
    if (unmountedRef.current || !roomId) return;

    // The browser sends the HttpOnly session cookie with the WS upgrade
    // request automatically — no ?token= query parameter needed.
    const ws = new WebSocket(getWsUrl(roomId));
    wsRef.current = ws;

    ws.onopen = () => {
      if (unmountedRef.current) { ws.close(); return; }
      setConnected(true);
      delayRef.current = INITIAL_DELAY;
    };

    ws.onmessage = (evt) => {
      try {
        const payload = JSON.parse(evt.data);
        onEventRef.current?.(payload);
      } catch (_) {/* ignore malformed frames */}
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      if (unmountedRef.current) return;
      timerRef.current = setTimeout(() => {
        delayRef.current = Math.min(delayRef.current * 2, MAX_DELAY);
        open();
      }, delayRef.current);
    };

    ws.onerror = () => { ws.close(); };
  }, [roomId]);

  useEffect(() => {
    unmountedRef.current = false;
    open();
    return () => {
      unmountedRef.current = true;
      clearTimeout(timerRef.current);
      wsRef.current?.close();
    };
  }, [open]);

  const sendMessage = useCallback((content: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', content }));
    }
  }, []);

  const sendTyping = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  }, []);

  const markRead = useCallback((messageId: number | string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'read', message_id: messageId }));
    }
  }, []);

  return { connected, sendMessage, sendTyping, markRead };
}
